import type { SystemStyleObject } from '@chakra-ui/system';
import React from 'react';

import { subtreeContainsSuggestionLeaf } from '@frontend/components';
import { useCascaderState } from '@frontend/components/src/components/forms/cascader/context';
import {
  LeafOption,
  SearchOption,
  SubmenuOption,
} from '@frontend/components/src/components/forms/cascader/subcomponents';
import type { CascaderComponentProps } from '@frontend/components/src/components/forms/cascader/types';
import { isSubmenu } from '@frontend/components/src/components/forms/cascader/util';
import { AssistanceIcon } from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';

import type { MaterialCascaderSuggestionOption } from './materialCascaderSuggestionTypes';
import { materialSuggestionCascaderStyles } from './materialSuggestionCascaderStyles';

type P = CascaderComponentProps<MaterialCascaderSuggestionOption>;

const suggestionRowInteractionCss: SystemStyleObject = {
  _hover: {
    bg: 'interaction.surfaceSuggestionHover',
  },
  '&[data-highlighted]': {
    bg: 'interaction.surfaceSuggestionHover',
  },
  _active: {
    bg: 'interaction.surfaceSuggestionActive',
  },
};

const materialSuggestionSearchRowPadding: SystemStyleObject = {
  /** Keeps in-row icons clear of the scrollbar when `scrollbarGutter: stable` is used on the list. */
  pr: 2,
};

const mergeSuggestionMenuCss = (
  base: SystemStyleObject | undefined,
  applySuggestion: boolean,
): SystemStyleObject | undefined => {
  if (!applySuggestion) return base;
  return {
    ...(base && typeof base === 'object' ? base : {}),
    ...suggestionRowInteractionCss,
  };
};

const pathHasSuggestedBranchPrefix = (
  getOptionAtPath: (
    path: string[],
  ) => MaterialCascaderSuggestionOption | undefined,
  fullPath: string[],
): boolean => {
  for (let len = 1; len < fullPath.length; len++) {
    const node = getOptionAtPath(fullPath.slice(0, len));
    if (
      node &&
      isSubmenu(node) &&
      Boolean((node as { isSuggestedBranch?: boolean }).isSuggestedBranch)
    ) {
      return true;
    }
  }
  return false;
};

const getOptionFullPath = (
  option: MaterialCascaderSuggestionOption,
): string[] | undefined =>
  'fullPath' in option && Array.isArray(option.fullPath)
    ? option.fullPath
    : undefined;

export const MaterialSuggestionLeafOption = (props: P['LeafOption']) => {
  const { option, getCommonProps, __css, ...rest } = props;
  const { isDisabled } = useCascaderState<MaterialCascaderSuggestionOption>();
  const { getOptionAtPath } = getCommonProps();
  const isSuggestionLeaf = Boolean(option.isSuggestion);
  const fullPath = getOptionFullPath(option);
  const underSuggestedBranch =
    fullPath !== undefined &&
    pathHasSuggestedBranchPrefix(getOptionAtPath, fullPath);
  const suggestionSurface = isSuggestionLeaf || underSuggestedBranch;
  const showAssistanceIcon = isSuggestionLeaf;
  return (
    <LeafOption
      {...rest}
      option={option}
      getCommonProps={getCommonProps}
      rightAddon={
        showAssistanceIcon ? (
          <AssistanceIcon
            __css={materialSuggestionCascaderStyles.optionSuggestionIconInline}
            color={
              isDisabled ? 'interaction.disabled' : 'interaction.suggestion'
            }
            pointerEvents="none"
            aria-hidden
          />
        ) : undefined
      }
      __css={mergeSuggestionMenuCss(__css, suggestionSurface)}
    />
  );
};

export const MaterialSuggestionSearchOption = (props: P['SearchOption']) => {
  const { option, getCommonProps, __css, ...rest } = props;
  const { isDisabled } = useCascaderState<MaterialCascaderSuggestionOption>();
  const { getOptionAtPath } = getCommonProps();
  const isSuggestionLeaf = Boolean(option.isSuggestion);
  const underSuggestedBranch = pathHasSuggestedBranchPrefix(
    getOptionAtPath,
    option.fullPath,
  );
  const suggestionSurface = isSuggestionLeaf || underSuggestedBranch;
  const showAssistanceIcon = isSuggestionLeaf;
  return (
    <SearchOption
      {...rest}
      option={option}
      getCommonProps={getCommonProps}
      rightAddon={
        showAssistanceIcon ? (
          <AssistanceIcon
            __css={materialSuggestionCascaderStyles.optionSuggestionIconInline}
            color={
              isDisabled ? 'interaction.disabled' : 'interaction.suggestion'
            }
            pointerEvents="none"
            aria-hidden
          />
        ) : undefined
      }
      __css={mergeSuggestionMenuCss(
        {
          ...(typeof __css === 'object' && __css ? __css : {}),
          ...materialSuggestionSearchRowPadding,
        },
        suggestionSurface,
      )}
    />
  );
};

export const MaterialSuggestionSubmenuOption = (props: P['SubmenuOption']) => {
  const { option, getCommonProps, __css, ...rest } = props;
  const { isDisabled } = useCascaderState<MaterialCascaderSuggestionOption>();
  const branchShowsSuggestionHint = subtreeContainsSuggestionLeaf(option);
  return (
    <SubmenuOption
      {...rest}
      option={option}
      getCommonProps={getCommonProps}
      rightAddon={
        branchShowsSuggestionHint ? (
          <AssistanceIcon
            __css={materialSuggestionCascaderStyles.optionSuggestionIconInline}
            color={
              isDisabled ? 'interaction.disabled' : 'interaction.suggestion'
            }
            pointerEvents="none"
            aria-hidden
          />
        ) : undefined
      }
      __css={mergeSuggestionMenuCss(__css, branchShowsSuggestionHint)}
    />
  );
};
